import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { SuggestedReasonsPicker } from "./suggested-reason-picker";
import { useTranslation } from "react-i18next";
import { tokens } from "@/locales/translations/tokens";

type FeedbackFormValues = {
  reason: string;
};

type FeedbackModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { reason: string; suggestions: string[] }) => void;
  initialReason?: string;
};

export function MessageFeedbackModal({
  open,
  onClose,
  onSubmit,
  initialReason = "",
}: FeedbackModalProps) {
  const { t } = useTranslation();
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  const SUGGESTED_REASONS = [
    t(tokens.workstation.feedbackModal.suggestedReasons.incorrect),
    t(tokens.workstation.feedbackModal.suggestedReasons.outdated),
    t(tokens.workstation.feedbackModal.suggestedReasons.invented),
    t(tokens.workstation.feedbackModal.suggestedReasons.irrelevant), 
    t(tokens.workstation.feedbackModal.suggestedReasons.ignored),
    t(tokens.workstation.feedbackModal.suggestedReasons.generic),
    t(tokens.workstation.feedbackModal.suggestedReasons.shallow),
    t(tokens.workstation.feedbackModal.suggestedReasons.confusing), 
    t(tokens.workstation.feedbackModal.suggestedReasons.tooTechnical),
    t(tokens.workstation.feedbackModal.suggestedReasons.redundant),
    t(tokens.workstation.feedbackModal.suggestedReasons.biased), 
  ];

  const form = useForm<FeedbackFormValues>({
    defaultValues: {
      reason: initialReason,
    },
  });

  function toggleSuggestion(reason: string) {
    setSelectedSuggestions((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  }

  function handleSubmit(data: FeedbackFormValues) {
    onSubmit({
      reason: data.reason,
      suggestions: selectedSuggestions,
    });
    form.reset();
    setSelectedSuggestions([]);
  }

  function handleFormSubmissionOnEnter(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      form.handleSubmit(handleSubmit)();
    }
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-2xl sm:rounded-lg sm:mt-10">
        <DialogHeader>
          <DialogTitle>{t(tokens.workstation.feedbackModal.suggestedReasons.title)}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <SuggestedReasonsPicker
              options={SUGGESTED_REASONS}
              selectedSuggestions={selectedSuggestions}
              onToggle={toggleSuggestion}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={t(tokens.workstation.feedbackModal.input.placeholder)}
                      className="resize-none"
                      onKeyDown={handleFormSubmissionOnEnter}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t(tokens.workstation.feedbackModal.cancel)}
              </Button>
              <Button type="submit">{t(tokens.workstation.feedbackModal.sendFeedback)}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}